/** @jsxImportSource @emotion/react */
import React, { useState } from 'react'
import { css } from '@emotion/react'
import { Switch, Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import {
  DIVISION_CONSTRAINT_CODES,
  HARD_CONSTRAINT_TOOLTIP,
  SOFT_CONSTRAINT_TOOLTIP,
  getConstraintDisplayName,
  formatConstraintParameterSummary,
} from '../../constants'
import type {
  SchedulerConstraint,
  CreateConstraintData,
} from '../../hooks/useSchedulerConstraints'
import type { Venue } from '../../hooks/useSchedulerLocations'
import InlineConstraintForm from './InlineConstraintForm'

interface DivisionConstraintsTableProps {
  constraints: SchedulerConstraint[]
  divisionId: string
  onToggleEnabled?: (constraintId: string, enabled: boolean) => void
  onAddConstraint?: (data: CreateConstraintData) => Promise<unknown>
  onDeleteConstraint?: (constraintId: string) => Promise<void>
  onUpdateConstraintType?: (constraintId: string, isHard: boolean) => void
  venues?: Venue[]
}

const DivisionConstraintsTable: React.FC<DivisionConstraintsTableProps> = ({
  constraints,
  divisionId,
  onToggleEnabled,
  onAddConstraint,
  onDeleteConstraint,
  onUpdateConstraintType,
  venues,
}) => {
  const [showAddForm, setShowAddForm] = useState(false)

  const divisionConstraints = constraints.filter(
    (c) =>
      DIVISION_CONSTRAINT_CODES.includes(
        c.type.code as (typeof DIVISION_CONSTRAINT_CODES)[number]
      ) &&
      (c.division_uids === null ||
        (c.division_uids && c.division_uids.includes(divisionId)))
  )

  return (
    <div css={sectionContainer}>
      {/* Section header */}
      <div css={sectionHeader}>
        <div css={sectionTitleGroup}>
          <span css={sectionTitle}>DIVISION CONSTRAINTS</span>
          <span css={sectionSubtitle}>
            Set scheduling rules like venue preferences, time restrictions, and
            matchup limits
          </span>
        </div>
        <div css={headerButtons}>
          <button css={addButton} onClick={() => setShowAddForm(true)}>
            <i className='fas fa-plus' css={addBtnIcon} />
            <span>Add</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div css={tableWrapper}>
        {/* Header */}
        <div css={tableHeaderRow}>
          <div css={[headerCellStyle, numCol]}>#</div>
          <div css={[headerCellStyle, nameCol]}>CONSTRAINTS</div>
          <div css={[headerCellStyle, typeCol]}>TYPE</div>
          <div css={[headerCellStyle, categoryCol]}>CATEGORY</div>
          <div css={[headerCellStyle, activeCol]}>ACTIVE</div>
          <div css={[headerCellStyle, actionsColStyle, actionsHidden]}>
            Actions
          </div>
        </div>

        {/* Inline add form */}
        {showAddForm && onAddConstraint && (
          <InlineConstraintForm
            divisionId={divisionId}
            tier='division'
            onSave={async (data) => {
              await onAddConstraint(data)
              setShowAddForm(false)
            }}
            onCancel={() => setShowAddForm(false)}
            venues={venues}
          />
        )}

        {/* Body */}
        {divisionConstraints.length === 0 && !showAddForm ? (
          <div css={emptyState}>No constraints for this division</div>
        ) : (
          divisionConstraints.map((constraint, index) => (
            <div key={constraint.id} css={tableRow}>
              <div css={[bodyCellStyle, numCol]}>
                <span css={cellText}>{index + 1}</span>
              </div>
              <div css={[bodyCellStyle, nameCol, nameColLayout]}>
                <span css={constraintNameText}>
                  {getConstraintDisplayName(
                    constraint.type?.code || '',
                    constraint.type?.name || 'Unnamed'
                  )}
                </span>
                {(() => {
                  const paramSummary = formatConstraintParameterSummary(
                    constraint.type?.code || '',
                    constraint.parameters as Record<string, unknown>
                  )
                  const subtitle = paramSummary || constraint.description
                  return subtitle ? (
                    <span css={constraintDescText}>{subtitle}</span>
                  ) : null
                })()}
              </div>
              <div css={[bodyCellStyle, typeCol]}>
                <div css={typeBadgeGroup}>
                  <Tooltip title={HARD_CONSTRAINT_TOOLTIP}>
                    <span
                      css={
                        constraint.is_hard ? hardBadgeActive : hardBadgeInactive
                      }
                      onClick={() =>
                        onUpdateConstraintType?.(constraint.id, true)
                      }
                    >
                      HARD
                    </span>
                  </Tooltip>
                  <Tooltip title={SOFT_CONSTRAINT_TOOLTIP}>
                    <span
                      css={
                        constraint.is_hard ? softBadgeInactive : softBadgeActive
                      }
                      onClick={() =>
                        onUpdateConstraintType?.(constraint.id, false)
                      }
                    >
                      SOFT
                    </span>
                  </Tooltip>
                </div>
              </div>
              <div css={[bodyCellStyle, categoryCol]}>
                <span css={categoryBadge}>
                  {(constraint.type?.category || '-').toUpperCase()}
                </span>
              </div>
              <div css={[bodyCellStyle, activeCol]}>
                <Switch
                  size='small'
                  checked={constraint.is_enabled}
                  onChange={(checked) =>
                    onToggleEnabled?.(constraint.id, checked)
                  }
                  css={switchStyles}
                />
              </div>
              <div css={[bodyCellStyle, actionsColStyle]}>
                <div css={actionIcons}>
                  <Tooltip title='Clone Constraint'>
                    <button css={iconBtn}>
                      <i className='fas fa-plus' />
                    </button>
                  </Tooltip>
                  <Tooltip title='Edit Constraint'>
                    <button css={iconBtn}>
                      <i className='fas fa-pen' />
                    </button>
                  </Tooltip>
                  <Tooltip title='Delete Constraint'>
                    <button
                      css={iconBtnDanger}
                      onClick={() => onDeleteConstraint?.(constraint.id)}
                    >
                      <i className='fas fa-trash' />
                    </button>
                  </Tooltip>
                  <Tooltip title='More Options'>
                    <button css={iconBtn}>
                      <i className='fas fa-ellipsis-v' />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Section header ──────────────────────────────────
const sectionContainer = css`
  margin-top: 24px;
`

const sectionHeader = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`

const sectionTitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.68px;
  color: ${colors.WHITE};
  text-transform: uppercase;
`

const sectionTitleGroup = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const sectionSubtitle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  color: ${colors.NEUTRAL_50};
  font-weight: 400;
  line-height: 17.4px;
`

const headerButtons = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

const addButton = css`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border: 1px solid ${colors.PRIMARY_300};
  border-radius: 8px;
  background: transparent;
  color: ${colors.PRIMARY_300};
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${colors.PRIMARY_300}0D;
  }
`

const addBtnIcon = css`
  font-size: 12px;
`

// ─── Table shell ─────────────────────────────────────
const tableWrapper = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

// ─── Column widths (aligned with DivisionTeamsTable) ─
const numCol = css`
  flex-shrink: 0;
  justify-content: center;
  padding-left: 16px;
  width: 32px;
`
const nameCol = css`
  flex: 1;
  min-width: 0;
`
const nameColLayout = css`
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
`
const typeCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const categoryCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const activeCol = css`
  width: 112px;
  flex-shrink: 0;
  justify-content: center;
`
const actionsColStyle = css`
  width: 184px;
  flex-shrink: 0;
  padding-left: 16px;
  padding-right: 16px;
`

// ─── Table header row ────────────────────────────────
const tableHeaderRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 36px;
  background-color: #212935;
  border-bottom: 1px solid ${colors.SECONDARY_300};
`

const headerCellStyle = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.6px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
  white-space: nowrap;
  padding: 8px 0;
  display: flex;
  align-items: center;
`

const actionsHidden = css`
  opacity: 0;
`

// ─── Table body row ──────────────────────────────────
const tableRow = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-left: 36px;
  background-color: ${colors.SECONDARY_400};

  &:hover {
    background-color: #212935;
  }
`

const bodyCellStyle = css`
  padding: 8px;
  display: flex;
  align-items: center;
`

const cellText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_0};
`

const constraintNameText = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: ${colors.WHITE};
`

const constraintDescText = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_100};
`

// ─── Type dual badge ─────────────────────────────────
const typeBadgeGroup = css`
  display: flex;
  border-radius: 4px;
  overflow: hidden;
`

const typeBadgeBase = css`
  font-family: 'Inter', sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1.6px;
  line-height: 12px;
  text-transform: uppercase;
  padding: 4px 8px;
  white-space: nowrap;
  cursor: pointer;
`

const hardBadgeActive = css`
  ${typeBadgeBase};
  background-color: ${colors.ERROR_500};
  color: ${colors.WHITE};
`

const hardBadgeInactive = css`
  ${typeBadgeBase};
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
  opacity: 0.5;
`

const softBadgeActive = css`
  ${typeBadgeBase};
  background-color: ${colors.INFO_500};
  color: ${colors.NEUTRAL_30};
`

const softBadgeInactive = css`
  ${typeBadgeBase};
  background-color: ${colors.SECONDARY_400};
  color: ${colors.WHITE};
  opacity: 0.5;
`

// ─── Category badge ──────────────────────────────────
const categoryBadge = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.6px;
  line-height: 16px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  background-color: #323a48;
  border-radius: 4px;
  padding: 4px 8px;
  white-space: nowrap;
`

// ─── Switch override ─────────────────────────────────
const switchStyles = css`
  &.ant-switch-checked {
    background-color: ${colors.PRIMARY_300} !important;
  }
`

// ─── Action icons ────────────────────────────────────
const actionIcons = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

const iconBtn = css`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_100};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    color: ${colors.WHITE};
  }
`

const iconBtnDanger = css`
  background: transparent;
  border: none;
  color: ${colors.ERROR_300};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;

  &:hover {
    opacity: 0.7;
  }
`

// ─── Empty state ─────────────────────────────────────
const emptyState = css`
  padding: 24px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.NEUTRAL_100};
`

export default DivisionConstraintsTable
